/**
 * The fixture implementation of `CabinetApi`. It is what every screen runs against today.
 *
 * IT IS NOT A STUB THAT ALWAYS SUCCEEDS, and that is the whole design. It pages by cursor exactly
 * as the platform does, it refuses a transition from the wrong state with the SAME `error_code` the
 * contract names, and it can be told to be slow, empty or broken. A screen whose refusal path was
 * never rendered is a screen whose refusal path is wrong.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: it does not invent a field. Every value it returns comes from
 * ./fixtures, which is annotated with the generated types, so the mock cannot drift from the
 * contract without failing to compile.
 *
 * THE CURSOR IS OPAQUE HERE TOO. The platform's cursor is server-issued and authenticated, and
 * "a client that parses a cursor is relying on an unpublished encoding and will break". So this
 * mock hands out a value the screens only ever pass back, never read — if a screen ever starts
 * decoding it, the real API will punish that, not this file.
 */
import {
  FIXTURE_AGREEMENTS,
  FIXTURE_APPLICATION,
  FIXTURE_ARTICLES,
  FIXTURE_ARTICLE_STATS,
  FIXTURE_ARTICLE_SUMMARY_EXTRAS,
  FIXTURE_AUDIENCE,
  FIXTURE_BIRTH_PLACES,
  FIXTURE_COMMENTS,
  FIXTURE_COUNTRIES,
  FIXTURE_DASHBOARD,
  FIXTURE_DASHBOARD_NO_DATA,
  FIXTURE_EARLIER_MONTHS,
  FIXTURE_MESSAGE_TRANSLATIONS,
  FIXTURE_NOTIFICATIONS,
  FIXTURE_NOTIFICATION_PREFERENCES,
  FIXTURE_OWN_PROFILE,
  FIXTURE_PAYMENTS,
  FIXTURE_PAYMENTS_NEGATIVE,
  FIXTURE_PAYOUT_METHOD,
  FIXTURE_PAYOUT_METHOD_UNSET,
  FIXTURE_RISING_SIGN,
  FIXTURE_SESSION,
  FIXTURE_SESSION_DISABLED,
  FIXTURE_STAFF_ARCHIVED,
  FIXTURE_STATEMENTS,
  FIXTURE_STATEMENT_PERIODS,
  FIXTURE_SUPPORTER_MESSAGES,
} from './fixtures';
import { daysIn } from '../ranges';
import {
  FIXTURE_NEW_STREAM_PREFIX,
  FIXTURE_NEW_VIDEO_PREFIX,
  FIXTURE_VIDEOS,
  emptyStreamDraft,
  emptyVideoDraft,
} from './fixture-video-desk';
import {
  ApiProblem,
  DELETION_ERROR_CODE,
  ERROR_CODE,
  NotFoundFailure,
  TransportFailure,
} from './problem';
import { PAYMENT_DETAILS_PARTS } from './cabinet-api';
import type { Agreement, CabinetApi, PaymentDetails, PaymentDetailsPart } from './cabinet-api';
import type {
  ArticleListItem,
  ArticleStatus,
  CabinetComment,
  Dashboard,
  ErrorCode,
  OperationQuery,
  OperationResult,
  Problem,
  PublicId,
  StatementSummary,
  StreamAccess,
  TransitionResult,
  VideoAccess,
} from './generated';

/**
 * Which world the fixtures live in. `default` is the working author; the others exist so a
 * developer can look at a state that is otherwise unreachable without a real backend.
 */
export type FixtureScenario =
  | 'default'
  /** Nothing published yet: empty lists and `no_data` tiles. An empty state is a screen, not a blank. */
  | 'empty'
  /** Every read fails at the transport. Proves the error state is readable and offers a retry. */
  | 'offline'
  /** An author on hold. The cabinet stays reachable; payments are refused with AUTHOR_DISABLED. */
  | 'disabled'
  /** A negative available balance and an unpaid first article. */
  | 'negative-balance';

export interface FixtureCabinetApiOptions {
  readonly scenario?: FixtureScenario;
  /** Milliseconds of pretend latency, so the loading states are visible rather than theoretical. */
  readonly latencyMs?: number;
  /** Whether staff let this author publish videos (v1.0.29). Granted unless a spec says otherwise. */
  readonly videoAccess?: VideoAccess;
  /** Whether staff let this author announce live streams (1.2.0). Granted unless a spec says so. */
  readonly streamAccess?: StreamAccess;
}

const DEFAULT_LATENCY_MS = 240;
const CURSOR_PREFIX = 'fixture-cursor:';

function problem(status: number, errorCode: ErrorCode, title: string, detail?: string): ApiProblem {
  const body: Problem = {
    type: `https://moonx.art/problems/${errorCode.toLowerCase().replaceAll('_', '-')}`,
    title,
    status,
    error_code: errorCode,
    ...(detail === undefined ? {} : { detail }),
  };
  return new ApiProblem(body);
}

/**
 * Keyset paging over an in-memory array.
 *
 * OFFSET IS THE IMPLEMENTATION HERE AND THE CURSOR IS STILL OPAQUE, which is the honest way to
 * fake a facility whose real encoding is unpublished: the screens get a value they can only echo
 * back, so they cannot accidentally grow a dependency on what is inside it.
 */
function page<T>(
  items: readonly T[],
  cursor: string | undefined,
  limit: number | undefined,
): { items: readonly T[]; next_cursor: string | null } {
  const size = limit ?? 20;
  const start =
    cursor !== undefined && cursor.startsWith(CURSOR_PREFIX)
      ? Number.parseInt(cursor.slice(CURSOR_PREFIX.length), 10)
      : 0;
  const from = Number.isFinite(start) && start > 0 ? start : 0;
  const slice = items.slice(from, from + size);
  const nextIndex = from + slice.length;
  return {
    items: slice,
    next_cursor: nextIndex < items.length ? `${CURSOR_PREFIX}${String(nextIndex)}` : null,
  };
}

export function createFixtureCabinetApi(options: FixtureCabinetApiOptions = {}): CabinetApi {
  const scenario: FixtureScenario = options.scenario ?? 'default';
  const latencyMs = options.latencyMs ?? DEFAULT_LATENCY_MS;

  /**
   * A mutable copy, because the row actions have to actually do something. `archiveOwnArticle`
   * that returns 200 and leaves the list unchanged teaches the screen nothing about refreshing.
   */
  const articles: ArticleListItem[] = FIXTURE_ARTICLES.map((item) => ({ ...item }));
  /** The author's videos (v1.0.29): a kind of article, listed apart (`kind: 'video'`). */
  const videos: ArticleListItem[] = FIXTURE_VIDEOS.map((item) => ({ ...item }));
  /**
   * The videos ever sent for review. The platform keeps such a video's submission as evidence, so
   * it refuses to delete it even once withdrawn - the same 409 as a published article.
   */
  const sentVideos = new Set(
    videos.filter((item) => item.status !== 'draft').map((item) => item.articleId),
  );
  let videoAccess: VideoAccess = options.videoAccess ?? 'granted';
  let streamAccess: StreamAccess = options.streamAccess ?? 'granted';
  const comments: CabinetComment[] = FIXTURE_COMMENTS.map((item) => ({ ...item }));
  let replySeq = 0;
  let transitionSeq = 0;
  /**
   * The account's own state, mutable for the same reason the articles are: a switch that answers
   * 200 and reads back unchanged teaches the screen nothing. `payoutMethod` starts as the scenario's
   * read, so a write is what the next `getPayoutMethod` returns - with its completeness worked out
   * again from the record and the agreements on every read (`presentPaymentDetails`).
   */
  let payoutMethod: PaymentDetails =
    scenario === 'negative-balance'
      ? { ...FIXTURE_PAYOUT_METHOD_UNSET }
      : { ...FIXTURE_PAYOUT_METHOD };
  let agreements: readonly Agreement[] = FIXTURE_AGREEMENTS.items.map((item) => ({ ...item }));
  let profile: OperationResult<'getOwnProfile'> = { ...FIXTURE_OWN_PROFILE };
  let deletionRequested = false;
  let notifications: NotificationRow[] = FIXTURE_NOTIFICATIONS.map((item) => ({ ...item }));
  let preferences: OperationResult<'getNotificationPreferences'> = {
    items: FIXTURE_NOTIFICATION_PREFERENCES.items.map((item) => ({
      ...item,
      channels: item.channels.map((channel) => ({ ...channel })),
    })),
  };
  /** The readers' messages, mutable for the same reason: a hide must read back hidden. */
  let messages: SupporterMessageRow[] = FIXTURE_SUPPORTER_MESSAGES.map((item) => ({ ...item }));

  /**
   * What the server's `ArticleCardReader` joins onto a listed comment or message: the title, and the
   * live page's path while the article is published (FIXTURE_ARTICLE_SUMMARY_EXTRAS). The desk's
   * published pieces are its statistics rows; one listed with another status has no live page. The
   * fixture's articles have no covers, so no picture.
   */
  function articleCard(articleId: string | null | undefined): {
    readonly articleTitle?: string;
    readonly articleWebPath?: string;
  } {
    if (articleId === null || articleId === undefined) {
      return {};
    }
    const listed = articles.find((item) => item.articleId === articleId);
    const title =
      listed?.title ?? FIXTURE_ARTICLE_STATS.find((item) => item.articleId === articleId)?.title;
    const webPath =
      listed === undefined || listed.status === 'published'
        ? (FIXTURE_ARTICLE_SUMMARY_EXTRAS.get(articleId)?.webPath ?? null)
        : null;
    return {
      ...(title === undefined ? {} : { articleTitle: title }),
      ...(webPath === null ? {} : { articleWebPath: webPath }),
    };
  }

  function settle<T>(value: () => T, signal?: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (scenario === 'offline') {
          reject(new TransportFailure('The request did not reach the platform.'));
          return;
        }
        try {
          resolve(value());
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      }, latencyMs);
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        },
        { once: true },
      );
    });
  }

  function findArticle(articleId: PublicId): ArticleListItem {
    const found =
      articles.find((item) => item.articleId === articleId) ??
      videos.find((item) => item.articleId === articleId);
    if (found === undefined) {
      throw new NotFoundFailure('That article is no longer there. Reload the list.');
    }
    return found;
  }

  /** The list a row is on: an article's, or a video's. */
  function listOf(article: ArticleListItem): ArticleListItem[] {
    return videos.includes(article) ? videos : articles;
  }

  /** A message addressed to this author. Another author's is the same 404 as none (hide/unhide/retract). */
  function findMessage(messageId: PublicId): SupporterMessageRow {
    const found = messages.find((item) => item.supporterMessageId === messageId);
    if (found === undefined) {
      throw new NotFoundFailure('That message is no longer there. Reload the list.');
    }
    return found;
  }

  function replaceMessage(updated: SupporterMessageRow): SupporterMessageRow {
    messages = messages.map((item) =>
      item.supporterMessageId === updated.supporterMessageId ? updated : item,
    );
    return updated;
  }

  /** The author's own flag only - `hiddenByAdmin` is the editors' and is never touched here. */
  function setAuthorHide(messageId: PublicId, hidden: boolean): SupporterMessageRow {
    const message = findMessage(messageId);
    if (message.visibility.hiddenByAuthor === hidden) {
      throw problem(
        409,
        'CONFLICT_UNSPECIFIED',
        'Conflict',
        hidden ? 'You have already hidden this message.' : 'You have not hidden this message.',
      );
    }
    return replaceMessage({
      ...message,
      visibility: { ...message.visibility, hiddenByAuthor: hidden },
    });
  }

  /**
   * Every author-side move answers with a `TransitionResult`, which is the MOVE and not the row —
   * no title, no cover. So the screen re-reads the list afterwards rather than patching a row from
   * this, which is also the only thing that keeps a second tab's changes from disappearing.
   */
  function transition(
    articleId: PublicId,
    allowedFrom: ArticleStatus,
    to: ArticleStatus,
    conflict: { code: ErrorCode; title: string },
  ): TransitionResult {
    const article = findArticle(articleId);
    if (article.status !== allowedFrom) {
      throw problem(409, conflict.code, conflict.title);
    }
    const updated: ArticleListItem = { ...article, status: to };
    const list = listOf(article);
    list.splice(list.indexOf(article), 1, updated);
    transitionSeq += 1;
    return {
      articleId,
      fromStatus: allowedFrom,
      status: to,
      seq: transitionSeq,
      occurredAt: FIXTURE_PAYMENTS.computedAt,
      ...(article.publishedAt === undefined ? {} : { publishedAt: article.publishedAt }),
      ...(article.publicationOrdinal === undefined
        ? {}
        : { publicationOrdinal: article.publicationOrdinal }),
    };
  }

  return {
    getAuthorSession: (signal) =>
      settle(
        () => ({
          ...(scenario === 'disabled' ? FIXTURE_SESSION_DISABLED : FIXTURE_SESSION),
          videoAccess,
          streamAccess,
        }),
        signal,
      ),

    getDashboard: (query, signal) =>
      settle(
        () =>
          withPeriodFigures(
            scenario === 'empty' ? FIXTURE_DASHBOARD_NO_DATA : FIXTURE_DASHBOARD,
            query,
          ),
        signal,
      ),

    listArticleStats: (query, signal) =>
      settle(() => {
        const source = scenario === 'empty' ? [] : FIXTURE_ARTICLE_STATS;
        const paged = page(source, query?.cursor, query?.limit);
        return {
          ...withAskedDays({ from: FIXTURE_DASHBOARD.from, to: FIXTURE_DASHBOARD.to }, query),
          items: paged.items,
          next_cursor: paged.next_cursor,
        };
      }, signal),

    getArticleSummary: (articleId, signal) =>
      settle(() => {
        // The stats list's row of the same article, so the two never disagree; its tips and its
        // path from FIXTURE_ARTICLE_SUMMARY_EXTRAS.
        const row = FIXTURE_ARTICLE_STATS.find((item) => item.articleId === articleId);
        const extras = FIXTURE_ARTICLE_SUMMARY_EXTRAS.get(articleId);
        const tile = (figure: { readonly state: string; readonly value?: number | null }) =>
          figure.state === 'ok' ? (figure.value ?? 0) : 0;
        return {
          articleId,
          views: row?.allTimeViews ?? 0,
          likes: row === undefined ? 0 : tile(row.likes),
          comments: row === undefined ? 0 : tile(row.comments),
          tipsMinor: extras?.tipsMinor ?? 0,
          tipsCurrency: (extras?.tipsMinor ?? 0) === 0 ? null : 'USD',
          webPath: extras?.webPath ?? null,
        };
      }, signal),

    listOwnArticles: (query, signal) =>
      settle(() => {
        const wanted = query?.status;
        // Articles when `kind` is absent, as the platform answers.
        const kind = query?.kind === 'video' ? videos : articles;
        const source =
          scenario === 'empty'
            ? []
            : wanted === undefined || wanted.length === 0
              ? kind
              : kind.filter((item) => wanted.includes(item.status));
        const paged = page(source, query?.cursor, query?.limit);
        return { total: source.length, items: paged.items, next_cursor: paged.next_cursor };
      }, signal),

    createDraft: () =>
      settle(() => {
        if (scenario === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'Your author account is on hold, so the editor is closed.',
          );
        }
        const articleId = `art_fixture_${String(articles.length + 1)}`;
        articles.unshift({
          articleId,
          status: 'draft',
          title: 'Untitled',
          awaitingCover: true,
          updatedAt: FIXTURE_PAYMENTS.computedAt,
        });
        // The 201 body is "the new working copy, EMPTY". Every required field of `WorkingCopy` is
        // present and empty rather than cast into shape — the list screen only reads `articleId`,
        // but a mock that lies about the body is a mock the editor cannot later reuse.
        return {
          articleId,
          status: 'draft',
          revision: 0,
          title: '',
          subtitle: '',
          blocks: [],
          wordCount: 0,
          blockCount: 0,
          updatedAt: FIXTURE_PAYMENTS.computedAt,
        };
      }),

    // The platform's video calls (v1.0.29), with its refusal for an author staff did not let - and
    // a live stream's (1.2.0), by its own switch.
    createVideoDraft: (body) =>
      settle(() => {
        if (body?.stream === true) {
          if (streamAccess !== 'granted') {
            throw problem(
              403,
              'STREAM_ACCESS_REQUIRED',
              'Live streams are not open to you yet.',
              'staff have not let this author announce live streams',
            );
          }
          const articleId = `${FIXTURE_NEW_STREAM_PREFIX}${String(videos.length + 1)}`;
          videos.unshift({
            articleId,
            status: 'draft',
            title: '',
            awaitingCover: true,
            streamStartsAt: null,
            updatedAt: FIXTURE_PAYMENTS.computedAt,
          });
          return emptyStreamDraft(articleId, FIXTURE_PAYMENTS.computedAt);
        }
        if (videoAccess !== 'granted') {
          throw problem(
            403,
            'VIDEO_ACCESS_REQUIRED',
            'Video is not open to you yet.',
            'staff have not let this author publish videos',
          );
        }
        const articleId = `${FIXTURE_NEW_VIDEO_PREFIX}${String(videos.length + 1)}`;
        videos.unshift({
          articleId,
          status: 'draft',
          title: '',
          awaitingCover: true,
          updatedAt: FIXTURE_PAYMENTS.computedAt,
        });
        return emptyVideoDraft(articleId, FIXTURE_PAYMENTS.computedAt);
      }),

    // A published fixture video's figures; the seeded ones are unpublished, so they count nothing.
    listVideoStats: (signal) =>
      settle(
        () => ({
          items: videos.map((video) => ({
            articleId: video.articleId,
            title: video.title,
            status: video.status,
            publishedAt: video.publishedAt ?? null,
            source: null,
            durationMs: null,
            views: 0,
            likes: 0,
            comments: 0,
          })),
        }),
        signal,
      ),

    requestVideoAccess: () =>
      settle(() => {
        const session = scenario === 'disabled' ? FIXTURE_SESSION_DISABLED : FIXTURE_SESSION;
        // Only an approved author may ask: the platform's capability guard answers anyone else
        // before the ask is looked at, by the state they are in.
        if (session.authorStatus === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'This author account is disabled.',
            'This author account is disabled. The cabinet is still reachable; the editor is not.',
          );
        }
        if (session.authorStatus === 'applicant') {
          throw problem(403, 'AUTHOR_APPROVAL_REQUIRED', 'The author is not approved yet.');
        }
        // Asking again, or with access already granted, changes nothing.
        if (videoAccess === 'none') {
          videoAccess = 'requested';
        }
        return { ...session, videoAccess, streamAccess };
      }),

    // Asked as video is, with the same refusals for an author who is not approved.
    requestStreamAccess: () =>
      settle(() => {
        const session = scenario === 'disabled' ? FIXTURE_SESSION_DISABLED : FIXTURE_SESSION;
        if (session.authorStatus === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'This author account is disabled.',
            'This author account is disabled. The cabinet is still reachable; the editor is not.',
          );
        }
        if (session.authorStatus === 'applicant') {
          throw problem(403, 'AUTHOR_APPROVAL_REQUIRED', 'The author is not approved yet.');
        }
        if (streamAccess === 'none') {
          streamAccess = 'requested';
        }
        return { ...session, videoAccess, streamAccess };
      }),

    archiveOwnArticle: (articleId) =>
      settle(() =>
        transition(articleId, 'published', 'archived', {
          code: ERROR_CODE.wrongFromState,
          title: 'Only a published article can be archived.',
        }),
      ),

    unarchiveOwnArticle: (articleId) =>
      settle(() => {
        if (FIXTURE_STAFF_ARCHIVED.includes(articleId)) {
          throw problem(
            403,
            ERROR_CODE.archiveActorMismatch,
            'An editor archived this article, so you may not unarchive it.',
          );
        }
        return transition(articleId, 'archived', 'published', {
          code: ERROR_CODE.wrongFromState,
          title: 'Only an archived article can be unarchived.',
        });
      }),

    deleteNeverPublishedDraft: (articleId) =>
      settle(() => {
        const article = findArticle(articleId);
        if (article.publishedAt !== undefined && article.publishedAt !== null) {
          throw problem(
            409,
            ERROR_CODE.deleteOnlyNeverPublishedDraft,
            'This article has been published, so it cannot be deleted.',
          );
        }
        if (sentVideos.has(article.articleId)) {
          throw problem(
            409,
            ERROR_CODE.deleteOnlyNeverPublishedDraft,
            'This video was sent for review, so it cannot be deleted.',
          );
        }
        const list = listOf(article);
        list.splice(list.indexOf(article), 1);
      }),

    withdrawArticle: (articleId) =>
      settle(() =>
        transition(articleId, 'in_review', 'draft', {
          code: ERROR_CODE.verdictAlreadyRecorded,
          title: 'A verdict has already been recorded.',
        }),
      ),

    pullScheduledArticle: (articleId) =>
      settle(() =>
        transition(articleId, 'scheduled', 'draft', {
          code: ERROR_CODE.goLiveAlreadyPassed,
          title: 'Its go-live moment has passed.',
        }),
      ),

    listCommentsOnMyArticles: (query, signal) =>
      settle(() => {
        let source: readonly CabinetComment[] = scenario === 'empty' ? [] : comments;
        if (query?.articleId !== undefined) {
          source = source.filter((item) => item.articleId === query.articleId);
        }
        if (query?.unansweredOnly === true) {
          // "Only ROOT comments this author has not replied to. A convenience over the same rows,
          // never a different set." A reply is never its own unanswered row.
          source = source.filter(
            (item) =>
              (item.parentId === null || item.parentId === undefined) && !item.hasAuthorReply,
          );
        }
        const paged = page(source, query?.cursor, query?.limit);
        // `total` rides ONLY on the unanswered filter (`CabinetCommentPage`, decision-log row 213):
        // every root under the filter and the `articleId` narrowing, never the page's length.
        return {
          ...(query?.unansweredOnly === true ? { total: source.length } : {}),
          items: paged.items.map((item) => ({ ...articleCard(item.articleId), ...item })),
          next_cursor: paged.next_cursor,
        };
      }, signal),

    replyToComment: (commentId, body) =>
      settle(() => {
        if (scenario === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'Your author account is on hold, so replies are refused.',
          );
        }
        const target = comments.find((item) => item.commentId === commentId);
        if (target === undefined) {
          throw new NotFoundFailure('That comment is no longer there. Reload the list.');
        }
        if (target.parentId !== null && target.parentId !== undefined) {
          throw problem(409, ERROR_CODE.replyHasNoReplies, 'A reply cannot itself be replied to.');
        }
        replySeq += 1;
        const reply: CabinetComment = {
          commentId: `cmt_fixture_${String(replySeq)}`,
          articleId: target.articleId,
          ...(target.articleTitle === undefined ? {} : { articleTitle: target.articleTitle }),
          parentId: target.commentId,
          body: body.body,
          // The mock has no clock either — a reply is stamped from the fixture's own instant.
          createdAt: FIXTURE_PAYMENTS.computedAt,
          author: FIXTURE_SESSION.displayName ?? 'You',
          isArticleAuthor: true,
          hasAuthorReply: false,
        };
        comments.splice(comments.indexOf(target) + 1, 0, reply);
        comments.splice(comments.indexOf(target), 1, { ...target, hasAuthorReply: true });
        return reply;
      }),

    getPaymentsView: (signal) =>
      settle(() => {
        if (scenario === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'Your author account is on hold. Payout actions are refused; the rest of the cabinet is open.',
          );
        }
        return scenario === 'negative-balance' ? FIXTURE_PAYMENTS_NEGATIVE : FIXTURE_PAYMENTS;
      }, signal),

    listStatements: (query, signal) =>
      settle(() => {
        // A ROW IS NOT A STATEMENT. The list carries period, what settled, the currency, the payout
        // status and when the document was rendered — and no text and no lines. Reading the
        // document is a second request, which is exactly why the screen opens a panel.
        const source: readonly StatementSummary[] =
          scenario === 'empty'
            ? []
            : FIXTURE_STATEMENT_PERIODS.flatMap((period) => {
                const statement = FIXTURE_STATEMENTS[period];
                if (statement === undefined) {
                  return [];
                }
                const payout = FIXTURE_PAYMENTS.payouts?.find((item) => item.period === period);
                return [
                  {
                    period: statement.period,
                    settledAmountMinor:
                      statement.totals.earningsMinor + statement.totals.awardMinor,
                    currency: statement.currency,
                    status: payout?.status ?? null,
                    renderedAt: statement.renderedAt,
                  },
                ];
              });
        const paged = page(source, query?.cursor, query?.limit);
        return { items: paged.items, next_cursor: paged.next_cursor };
      }, signal),

    getStatement: (period, signal) =>
      settle(() => {
        const statement = FIXTURE_STATEMENTS[period];
        if (statement === undefined) {
          throw new NotFoundFailure(`There is no statement for ${period}.`);
        }
        return statement;
      }, signal),

    getStatementCsv: (period, signal) =>
      settle(() => {
        const statement = FIXTURE_STATEMENTS[period];
        if (statement === undefined) {
          throw new NotFoundFailure(`There is no statement for ${period}.`);
        }
        // The CSV representation is "the same rows the CSV representation exports" — the run's
        // per-article lines. The real one is rendered server-side; this is the same columns so the
        // download button has something honest to hand over.
        const header =
          'article_id,publication_ordinal,published_at,countable_views,countable_likes,countable_comments,computed_amount_minor,settlement,award_minor,settled_amount_minor,currency';
        const rows = statement.lines.map((line) =>
          [
            line.articleId,
            line.publicationOrdinal ?? '',
            line.publishedAt ?? '',
            line.countableViews ?? '',
            line.countableLikes ?? '',
            line.countableComments ?? '',
            line.computedAmountMinor ?? '',
            line.settlement,
            line.awardMinor ?? '',
            line.settledAmountMinor,
            line.currency,
          ].join(','),
        );
        return [header, ...rows].join('\n');
      }, signal),

    getPayoutMethod: (signal) =>
      settle(() => presentPaymentDetails(payoutMethod, agreements), signal),

    // ---- the account's own screens ------------------------------------------------------------

    // NO RE-AUTHENTICATION HERE, deliberately: the fixture has no token and so no sign-in time to
    // judge, and a fixture that refused every first save would put a sign-in step in front of every
    // default-build visit. The 403 REAUTH_REQUIRED path is driven by the screen's own spec.
    updatePayoutMethod: (body) =>
      settle(() => {
        if (scenario === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'Your author account is on hold. Payout actions are refused; the rest of the cabinet is open.',
          );
        }
        const email = body.paypal?.email;
        if (body.methodKind !== 'paypal' || email === undefined) {
          throw problem(422, 'UNPROCESSABLE_UNSPECIFIED', 'PayPal needs an e-mail address.');
        }
        const country = body.recipientCountry ?? null;
        if (country !== null && !FIXTURE_COUNTRIES.items.some((item) => item.code === country)) {
          throw problem(422, 'UNPROCESSABLE_UNSPECIFIED', 'That country is not on the list.');
        }
        const legalName = body.legalName?.trim();
        if (legalName !== undefined && (legalName.length === 0 || legalName.length > 200)) {
          throw problem(400, 'REQUEST_VALIDATION_FAILED', 'A legal name is 1 to 200 characters.');
        }
        // Each save is a new version (design 25, P1): what was sent, and nothing kept from before -
        // so a save without "these details are true" is a version nobody confirmed.
        payoutMethod = {
          isSet: true,
          methodKind: 'paypal',
          recipientCountry: country,
          legalName: legalName ?? null,
          paypal: { maskedEmail: maskAddress(email), email },
          detailsConfirmedAt: body.detailsTrue === true ? FIXTURE_PAYMENTS.computedAt : null,
          requiredFrom: FIXTURE_PAYOUT_METHOD.requiredFrom ?? null,
          createdAt: payoutMethod.createdAt ?? FIXTURE_PAYMENTS.computedAt,
          updatedAt: FIXTURE_PAYMENTS.computedAt,
        };
        return presentPaymentDetails(payoutMethod, agreements);
      }),

    getOwnProfile: (signal) => settle(() => profile, signal),

    setBirthData: (body) =>
      settle(() => {
        // The fixture computes nothing. The platform's own astrology library does that, server-side;
        // here a date yields the fixture's sun sign, a time and zone its moon sign, and a time with a
        // place this desk's search offered its rising sign - the platform's rule, since the rising
        // sign needs the instant AND the place's coordinates (`signs_need_their_inputs`).
        const withTime = body.birthTime !== undefined && body.birthTime !== null;
        const knownPlace = FIXTURE_BIRTH_PLACES.some(
          (place) => place.birthPlaceId === body.birthPlaceId,
        );
        const signs: OperationResult<'setBirthData'> = {
          birthDataOnFile: true,
          computedSignsPublic: body.computedSignsPublic ?? profile.signs.computedSignsPublic,
          sunSign: FIXTURE_OWN_PROFILE.signs.sunSign ?? null,
          moonSign: withTime ? (FIXTURE_OWN_PROFILE.signs.moonSign ?? null) : null,
          risingSign: withTime && knownPlace ? FIXTURE_RISING_SIGN : null,
          zodiacSystem: 'tropical',
        };
        profile = { ...profile, signs };
        return signs;
      }),

    // The platform's typeahead over this desk's three places: a case-blind match on any word's
    // start, at most `limit` (the contract's default is 5), and no match an empty list. Spaces alone
    // are answered empty, as the platform answers them without asking the service.
    searchBirthPlaces: (query, signal) =>
      settle(() => {
        const typed = query.q.trim().toLocaleLowerCase();
        const birthPlaces =
          typed === ''
            ? []
            : FIXTURE_BIRTH_PLACES.filter((place) =>
                place.birthPlace
                  .toLocaleLowerCase()
                  .split(/[\s,]+/u)
                  .some((word) => word.startsWith(typed)),
              ).slice(0, query.limit ?? 5);
        return { birthPlaces };
      }, signal),

    clearBirthData: () =>
      settle(() => {
        profile = {
          ...profile,
          signs: {
            birthDataOnFile: false,
            computedSignsPublic: false,
            sunSign: null,
            moonSign: null,
            risingSign: null,
            zodiacSystem: null,
          },
        };
      }),

    setComputedSignsVisibility: (body) =>
      settle(() => {
        if (body.enabled && !profile.signs.birthDataOnFile) {
          throw problem(
            422,
            'BIRTH_DATE_REQUIRED',
            'There is no birth date on file, so there is no sign to show.',
          );
        }
        profile = { ...profile, signs: { ...profile.signs, computedSignsPublic: body.enabled } };
        return profile.signs;
      }),

    setEarningsVisibility: (body) =>
      settle(() => {
        const state = { enabled: body.enabled, changedAt: FIXTURE_PAYMENTS.computedAt };
        profile = { ...profile, earningsVisibility: state };
        return state;
      }),

    requestAccountDeletion: (body) =>
      settle(() => {
        if (deletionRequested) {
          // The platform's own answer for a repeat: a 409 with the generic code, because the
          // contract names no domain code for it (account-deletion.service.ts).
          throw problem(
            409,
            'CONFLICT_UNSPECIFIED',
            'Conflict',
            'A deletion request for this account is already waiting to be applied.',
          );
        }
        // Decision 5 (design 28): a balance above $0.00 is lost with the account, so the platform
        // refuses until the request confirms it - the balance the Payments page of this desk shows.
        const balance = (
          scenario === 'negative-balance' ? FIXTURE_PAYMENTS_NEGATIVE : FIXTURE_PAYMENTS
        ).availableMinor;
        if (balance > 0 && body.forfeitBalance !== true) {
          throw problem(
            422,
            DELETION_ERROR_CODE.balanceForfeitConfirmationRequired,
            'Balance forfeit confirmation required',
            'This account still has a balance above $0.00, which is lost when the account is deleted.',
          );
        }
        deletionRequested = true;
        return {
          accepted: true,
          anonymisedBylineLabel: 'Former author',
          articlesRemainPublished: !body.requestArticleTakedown,
          moneyRecordsRetained: true,
        };
      }),

    listNotifications: (query, signal) =>
      settle(() => {
        let source: readonly NotificationRow[] = scenario === 'empty' ? [] : notifications;
        if (query?.unreadOnly === true) {
          source = source.filter((row) => row.readAt === undefined || row.readAt === null);
        }
        // No `total`: the feed is an append-heavy stream and the contract gives it none.
        const paged = page(source, query?.cursor, query?.limit);
        return { items: paged.items, next_cursor: paged.next_cursor };
      }, signal),

    markNotificationsRead: (body) =>
      settle(() => {
        // Every row produced AT OR BEFORE the cut-off. The instants are one format, UTC with a Z,
        // so their text orders as their moments do - and the mock still reads no clock.
        let markedRead = 0;
        notifications = notifications.map((row) => {
          const unread = row.readAt === undefined || row.readAt === null;
          if (unread && row.createdAt <= body.upTo) {
            markedRead += 1;
            return { ...row, readAt: FIXTURE_PAYMENTS.computedAt };
          }
          return row;
        });
        const unreadRemaining = notifications.filter(
          (row) => row.readAt === undefined || row.readAt === null,
        ).length;
        return { markedRead, unreadRemaining, upTo: body.upTo };
      }),

    getNotificationPreferences: (signal) => settle(() => preferences, signal),

    // ---- what readers send, and who they were ---------------------------------------------------

    listSupporterMessages: (query, signal) =>
      settle(() => {
        let source: readonly SupporterMessageRow[] = scenario === 'empty' ? [] : messages;
        if (query?.visibility !== undefined) {
          // The filter is the SENDER'S choice at write time (`initial_public`), not today's audience.
          const wanted = query.visibility === 'public';
          source = source.filter((row) => row.initialPublic === wanted);
        }
        if (query?.unansweredOnly === true) {
          // B2-INBOX: what waits for the author's one reply - none yet, not taken back by its
          // sender, hidden by nobody. The server's 7-day window is its clock; this desk has none.
          source = source.filter(
            (row) =>
              (row.reply === null || row.reply === undefined) &&
              !row.visibility.isRetracted &&
              !row.visibility.hiddenByAuthor &&
              !row.visibility.hiddenByAdmin,
          );
        }
        const paged = page(source, query?.cursor, query?.limit);
        return {
          total: source.length,
          items: paged.items.map((row) => ({ ...row, ...articleCard(row.articleId) })),
          next_cursor: paged.next_cursor,
        };
      }, signal),

    replyToSupporterMessage: (messageId, body) =>
      settle(() => {
        const message = findMessage(messageId);
        if (scenario === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'Your author account is on hold, so replies are refused.',
          );
        }
        // The platform's order: a message its sender took back is refused first, then a second reply.
        if (message.visibility.isRetracted) {
          throw problem(
            409,
            'CONFLICT_UNSPECIFIED',
            'Conflict',
            'The sender has retracted this message.',
          );
        }
        if (message.reply !== undefined && message.reply !== null) {
          throw problem(409, 'REPLY_ALREADY_EXISTS', 'This message already has a reply.');
        }
        const reply = {
          body: body.body,
          // The mock has no clock either: a reply is stamped from the fixture's own instant.
          postedAt: FIXTURE_PAYMENTS.computedAt,
          isRetracted: false,
          hiddenByAdmin: false,
        };
        replaceMessage({ ...message, reply });
        return reply;
      }),

    retractOwnReply: (messageId) =>
      settle(() => {
        const message = findMessage(messageId);
        if (message.reply === undefined || message.reply === null) {
          throw problem(409, 'CONFLICT_UNSPECIFIED', 'Conflict', 'There is no reply to retract.');
        }
        if (message.reply.isRetracted) {
          throw problem(
            409,
            'CONFLICT_UNSPECIFIED',
            'Conflict',
            'That reply is already retracted.',
          );
        }
        return replaceMessage({ ...message, reply: { ...message.reply, isRetracted: true } });
      }),

    hideSupporterMessage: (messageId) => settle(() => setAuthorHide(messageId, true)),

    unhideSupporterMessage: (messageId) => settle(() => setAuthorHide(messageId, false)),

    requestSupporterMessageTranslation: (messageId, body) =>
      settle(() => {
        const message = findMessage(messageId);
        const stored = (message.translations ?? []).find(
          (translation) => translation.targetLanguage === body.targetLanguage,
        );
        if (stored !== undefined) {
          return {
            messageId,
            targetLanguage: body.targetLanguage,
            state: 'produced' as const,
            translatedAt: stored.translatedAt ?? null,
            sourceLanguage: stored.sourceLanguage ?? null,
          };
        }
        // "The request enqueues and returns": the fixture's job finishes at once, so the text is
        // there on the NEXT list read - never in this answer, which is `queued` like the platform's.
        const canned = FIXTURE_MESSAGE_TRANSLATIONS[messageId];
        const produced =
          canned !== undefined && canned.targetLanguage === body.targetLanguage
            ? canned
            : {
                targetLanguage: body.targetLanguage,
                body: message.body,
                sourceLanguage: body.targetLanguage,
                translatedAt: FIXTURE_PAYMENTS.computedAt,
              };
        replaceMessage({ ...message, translations: [...(message.translations ?? []), produced] });
        return {
          messageId,
          targetLanguage: body.targetLanguage,
          state: 'queued' as const,
          translatedAt: null,
          sourceLanguage: null,
        };
      }),

    getAudienceBreakdown: (query, signal) =>
      settle(() => {
        const sealed = FIXTURE_AUDIENCE[query.month]?.[query.dimension];
        if (sealed === undefined) {
          throw problem(
            404,
            'MONTH_NOT_SEALED',
            'Not Found',
            `${query.month} has not been sealed yet. A month's audience is frozen after the month ends.`,
          );
        }
        // A brand-new author's sealed months hold nobody: the same month, no groups, no merged line.
        return scenario === 'empty'
          ? {
              month: sealed.month,
              dimension: sealed.dimension,
              buckets: [],
              sealedAt: sealed.sealedAt ?? null,
            }
          : sealed;
      }, signal),

    updateNotificationPreferences: (body) =>
      settle(() => {
        // The platform's two refusals, both 422 and both before anything is written.
        for (const change of body.items) {
          const category = preferences.items.find(
            (item) => item.categoryCode === change.categoryCode,
          );
          if (category === undefined || category.isAlwaysOn) {
            throw problem(
              422,
              'ALWAYS_ON_CATEGORY',
              'That notification is always on and cannot be switched off.',
            );
          }
          if (!category.channels.some((channel) => channel.channel === change.channel)) {
            throw problem(422, 'UNPROCESSABLE_UNSPECIFIED', 'That channel is not offered.');
          }
        }
        preferences = {
          items: preferences.items.map((item) => ({
            ...item,
            channels: item.channels.map((channel) => {
              const change = body.items.find(
                (candidate) =>
                  candidate.categoryCode === item.categoryCode &&
                  candidate.channel === channel.channel,
              );
              return change === undefined
                ? channel
                : {
                    ...channel,
                    enabled: change.enabled,
                    // Back to the default removes the stored row (NotificationPreferencesWrite).
                    usingDefault: change.enabled === channel.defaultEnabled,
                    updatedAt:
                      change.enabled === channel.defaultEnabled
                        ? null
                        : FIXTURE_PAYMENTS.computedAt,
                  };
            }),
          })),
        };
        return preferences;
      }),

    // ---- the author's earlier months (design 17) ------------------------------------------------

    // Refused as `getPaymentsView` is for an author on hold; none for an author with nothing before
    // the payouts here - the brand-new `negative-balance` author and the `empty` one.
    listEarlierMonths: (signal) =>
      settle(() => {
        if (scenario === 'disabled') {
          throw problem(
            403,
            ERROR_CODE.authorDisabled,
            'Your author account is on hold. Payout actions are refused; the rest of the cabinet is open.',
          );
        }
        return scenario === 'empty' || scenario === 'negative-balance'
          ? { months: [] }
          : FIXTURE_EARLIER_MONTHS;
      }, signal),

    // ---- the Statistics chart (design 18) ---------------------------------------------------

    getDashboardDays: (query, signal) =>
      settle(() => fixtureDays(query, scenario === 'empty'), signal),

    // ---- Payment details' agreements and countries (design 25) ------------------------------

    listAgreements: (signal) => settle(() => ({ items: agreements }), signal),

    // ONE TRANSACTION, as the platform's: every item is checked before anything is recorded, so a
    // version that is not in force refuses the whole request. One already accepted is a no-op.
    acceptAgreements: (body) =>
      settle(() => {
        const codes = body.accept.map((item) => item.documentCode);
        if (codes.length < 1 || codes.length > 4 || new Set(codes).size !== codes.length) {
          throw problem(
            400,
            'REQUEST_VALIDATION_FAILED',
            'Bad Request',
            'Accept one to four different documents.',
          );
        }
        // A document with no version in force (`live` null) has nothing to accept: a 404 too.
        for (const item of body.accept) {
          const document = agreements.find((one) => one.documentCode === item.documentCode);
          if (document?.live?.versionLabel !== item.versionLabel) {
            throw new NotFoundFailure(
              'That version is no longer the one in force. Reload the page.',
            );
          }
        }
        agreements = agreements.map((document) => {
          const live = document.live;
          const asked = codes.includes(document.documentCode);
          if (!asked || live === null || document.accepted?.versionLabel === live.versionLabel) {
            return document;
          }
          return {
            ...document,
            accepted: {
              versionLabel: live.versionLabel,
              acceptedAt: FIXTURE_PAYMENTS.computedAt,
              contentUrl: live.contentUrl,
            },
            owed: false,
            newerVersionWaiting: false,
          };
        });
        return { items: agreements };
      }),

    listCountries: (signal) => settle(() => FIXTURE_COUNTRIES, signal),

    // The desk's author is approved, so their application is too (design 25: the Overview reads
    // it for an applicant only).
    getApplication: (signal) => settle(() => FIXTURE_APPLICATION, signal),
  };
}

type NotificationRow = NonNullable<OperationResult<'listNotifications'>['items']>[number];
type SupporterMessageRow = NonNullable<OperationResult<'listSupporterMessages'>['items']>[number];

/**
 * `v***@example.com` for `vera@example.com`: the server's own mask
 * (`src/modules/authors/services/payout-method.presenter.ts`, `maskPayoutEmail`) - the first
 * character, `***`, then `@` and the whole domain; `***` alone before the `@` of a one-character
 * name, and `***` for a value with no usable `@`. Until design 25 this desk drew its own shape,
 * which no screen would ever have shown.
 */
function maskAddress(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) {
    return '***';
  }
  const local = email.slice(0, at);
  return `${local.length > 1 ? local.slice(0, 1) : ''}***@${email.slice(at + 1)}`;
}

/**
 * The record as the platform presents it (design 25, P5): what is missing, in the page's order, and
 * `complete` when nothing is. A payout document is missing while it is owed (P11); one with no
 * version in force is owed by nobody (P10), so it is never among them.
 */
function presentPaymentDetails(
  record: PaymentDetails,
  agreements: readonly Agreement[],
): PaymentDetails {
  const owed = (code: string): boolean =>
    agreements.some((document) => document.documentCode === code && document.owed);
  const absent = (value: string | null | undefined): boolean =>
    value === undefined || value === null || value === '';
  const parts: Readonly<Record<PaymentDetailsPart, boolean>> = {
    legal_name: absent(record.legalName),
    country: absent(record.recipientCountry),
    paypal_email: !record.isSet || absent(record.paypal?.email),
    details_true: absent(record.detailsConfirmedAt),
    payout_terms: owed('payout_terms'),
    payment_data_notice: owed('payment_data_notice'),
  };
  const missing = PAYMENT_DETAILS_PARTS.filter((part) => parts[part]);
  return { ...record, complete: missing.length === 0, missing };
}

/**
 * ★ THE DAYS A READ ASKED FOR, ECHOED (design 15). The Overview and Statistics now ask for a period
 * - "Last 7 days", "Last 30 days", "This month", counted back from the first answer's `to` - and the
 * platform answers with the days it was asked (`Dashboard.from` / `to` are the request's, resolved).
 * This desk used to answer every period with its one fixed pair of days, so in the default build
 * every chip printed the same dates. The figures stay the desk's own; only the days follow the
 * question. Written at the foot so that the lines above, cited by number, keep theirs.
 */
function withAskedDays<T extends { readonly from: string; readonly to: string }>(
  answer: T,
  query: { readonly from?: string | undefined; readonly to?: string | undefined } | undefined,
): T {
  return {
    ...answer,
    ...(query?.from === undefined ? {} : { from: query.from }),
    ...(query?.to === undefined ? {} : { to: query.to }),
  };
}

/**
 * ★ AND EACH PERIOD HAS ITS OWN FIGURES (2026-10-05, the owner's list after design 15, item 10).
 * Echoing only the days left every period with the desk's one set of figures, so every change on
 * the Overview and Statistics read "no change" in the default build, and the changes - the reason
 * the period chips exist - could not be looked at without a live stack. Now a read that asks for
 * days gets each figure of the period in proportion to how many days it asked for, and a period
 * that ends before the desk's own `to` - the "period before" both screens ask for beside the one
 * shown - gets each figure times its share below, so the changes differ: up, down and one flat.
 * A read that asks for no days (the screens' first one) is answered exactly as before. Followers'
 * total is not a figure of the period and is never scaled; a figure that is not `ok` is left alone
 * ("Not measured yet" stays words). Invented, like everything on this desk - it never reaches a
 * live build.
 *
 * ★ ALL-TIME VIEWS ARE NOT A FIGURE OF THE PERIOD EITHER (the owner, 2026-10-05): `allTimeViews`
 * is every view ever, so every period answers the desk's one figure - it rides along untouched
 * with the rest of the answer, and nothing below may scale it. Saves are a figure of the period
 * now that they are reported (2026-10-05), so they are scaled like the others, and rise.
 */
function withPeriodFigures(
  answer: Dashboard,
  query: { readonly from?: string | undefined; readonly to?: string | undefined } | undefined,
): Dashboard {
  const asked = withAskedDays(answer, query);
  if (query?.from === undefined || query.to === undefined) {
    return asked;
  }
  const length = daysIn(asked) / daysIn(answer);
  const before = asked.to < answer.to;
  const share = (earlier: number): number => length * (before ? earlier : 1);
  const tiles = asked.tiles;
  return {
    ...asked,
    tiles: {
      ...tiles,
      views: scaledTile(tiles.views, 'value', share(0.86)),
      countedViews: scaledTile(tiles.countedViews, 'value', share(0.9)),
      readers: scaledTile(tiles.readers, 'distinctReadersInRange', share(0.92)),
      likes: scaledTile(tiles.likes, 'value', share(1.08)),
      comments: scaledTile(tiles.comments, 'value', share(0.8)),
      saves: scaledTile(tiles.saves, 'value', share(0.85)),
      tips: scaledTile(tiles.tips, 'value', share(1)),
      supporterMessages: scaledTile(tiles.supporterMessages, 'value', share(0.75)),
      followers: scaledTile(
        scaledTile(tiles.followers, 'gainedInRange', share(0.7)),
        'lostInRange',
        share(1.3),
      ),
    },
  };
}

/** One number of an `ok` tile times `factor`, a whole count no lower than zero. */
function scaledTile<T extends { readonly state: string }>(
  tile: T,
  field: string,
  factor: number,
): T {
  const value: unknown = Reflect.get(tile, field);
  return tile.state === 'ok' && typeof value === 'number'
    ? { ...tile, [field]: Math.max(0, Math.round(value * factor)) }
    : tile;
}

/**
 * ★ EVERY DAY OF THE PERIOD WITH ITS FIGURES (design 18) - the desk's `getDashboardDays`. Each day
 * of the days asked (the desk's own period when none were), oldest first: a quiet weekday level that
 * moves a little from day to day, and a rise on the day one of the desk's articles was published that
 * fades over the days after - so the chart has a shape, its busiest day and its publication dots line
 * up, and two periods differ. One article's days (`articleId`) are a share of the author's, none
 * before it was published. Counted
 * views, readers, likes, comments and saves are fixed fractions of the views. The `empty` desk
 * answers every day with zeros, as a server does for an author with nothing counted. Invented, like
 * everything on this desk; at most 366 days, the contract's cap.
 */
function fixtureDays(
  query: OperationQuery<'getDashboardDays'> | undefined,
  empty: boolean,
): OperationResult<'getDashboardDays'> {
  const from = query?.from ?? FIXTURE_DASHBOARD.from;
  const to = query?.to ?? FIXTURE_DASHBOARD.to;
  const articleId = query?.articleId ?? null;
  const share =
    articleId === null
      ? 1
      : Math.max(0.05, ((articleId.charCodeAt(articleId.length - 1) % 7) + 2) / 12);
  const published = FIXTURE_ARTICLE_STATS.flatMap((row) =>
    row.publishedAt === null || (articleId !== null && row.articleId !== articleId)
      ? []
      : [epochOf(row.publishedAt.slice(0, 10))],
  );
  const first = epochOf(from);
  const last = Math.min(epochOf(to), first + 365);
  const days: OperationResult<'getDashboardDays'>['days'][number][] = [];
  for (let epoch = first; epoch <= last; epoch += 1) {
    const wobble = ((epoch * 37) % 23) / 23;
    const lift = published.reduce(
      (sum, day) => (epoch >= day ? sum + 900 * 0.55 ** (epoch - day) : sum),
      0,
    );
    // One article has no views before it was published.
    const unpublished = articleId !== null && published.every((day) => epoch < day);
    const views = empty || unpublished ? 0 : Math.round((180 + 120 * wobble + lift) * share);
    days.push({
      day: new Date(epoch * 86_400_000).toISOString().slice(0, 10),
      views,
      countedViews: Math.round(views * 0.66),
      readers: Math.round(views * 0.31),
      likes: Math.round(views * 0.035),
      comments: Math.round(views * 0.005),
      saves: Math.round(views * 0.008),
    });
  }
  return { from, to, articleId, days };
}

/** A `YYYY-MM-DD` day as whole days since 1970 - arithmetic on a day the desk was given, no clock. */
function epochOf(day: string): number {
  return Math.round(
    Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) /
      86_400_000,
  );
}
